import React, { createContext, useContext, useMemo } from 'react';

import { useLevelFocus, type UseLevelFocusResult } from '@/hooks/useLevelFocus';

const LevelFocusContext = createContext<UseLevelFocusResult | null>(null);

interface LevelFocusProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the student's durable syllabus focus to a page subtree.
 *
 * It is mounted only on `/practice` (including `/tasks`) and `/progress`.
 * Per-page instances remain consistent because the underlying hook shares one
 * react-query cache entry; unrelated routes pay no focus/options request.
 */
export const LevelFocusProvider: React.FC<LevelFocusProviderProps> = ({
  children,
}) => {
  const value = useLevelFocus();
  const {
    enabled,
    isLoading,
    isUpdating,
    value: focusValue,
    options,
    ownLevelStream,
    setValue,
  } = value;
  const memoized = useMemo(
    () => ({
      enabled,
      isLoading,
      isUpdating,
      value: focusValue,
      options,
      ownLevelStream,
      setValue,
    }),
    [
      enabled,
      isLoading,
      isUpdating,
      focusValue,
      options,
      ownLevelStream,
      setValue,
    ]
  );
  return (
    <LevelFocusContext.Provider value={memoized}>
      {children}
    </LevelFocusContext.Provider>
  );
};

/**
 * Without a provider, resolve to the own-pair default and render no control.
 * This keeps deliberately-unfocused surfaces and isolated tests inert.
 */
export const useLevelFocusContext = (): UseLevelFocusResult => {
  const ctx = useContext(LevelFocusContext);
  if (ctx) return ctx;
  return {
    enabled: false,
    isLoading: false,
    isUpdating: false,
    value: null,
    options: [],
    ownLevelStream: null,
    setValue: () => undefined,
  };
};
